import type { HTMLAttributes, ReactNode } from "react";
import { AlertCircle, Check, CheckCircle2, Info, TriangleAlert } from "lucide-react";
import { cn } from "../cn";

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-[var(--radius-card)] border border-line bg-paper shadow-[var(--shadow-card)]",
        className,
      )}
      {...rest}
    />
  );
}

export type AlertTone = "error" | "success" | "warning" | "info";
const tones: Record<AlertTone, { cls: string; Icon: typeof Info }> = {
  error: { cls: "border-danger/20 bg-danger-bg text-danger", Icon: AlertCircle },
  success: { cls: "border-success/20 bg-success-bg text-success", Icon: CheckCircle2 },
  warning: { cls: "border-warning/20 bg-warning-bg text-warning", Icon: TriangleAlert },
  info: { cls: "border-primary/20 bg-info-bg text-primary-dark", Icon: Info },
};

export function Alert({
  tone = "info",
  title,
  children,
  action,
  className,
}: {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const { cls, Icon } = tones[tone];
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      aria-live={tone === "error" ? "assertive" : "polite"}
      className={cn(
        "flex items-start gap-3 rounded-[var(--radius-control)] border px-4 py-3 text-sm",
        cls,
        className,
      )}
    >
      <Icon aria-hidden className="mt-0.5 size-5 shrink-0" />
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cn(title && "mt-0.5", "text-ink")}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

export type BadgeTone = "neutral" | "success" | "danger" | "warning" | "info";
const badgeTones: Record<BadgeTone, string> = {
  neutral: "bg-subtle text-ink-2 ring-line-strong/70",
  success: "bg-success-bg text-success ring-success/20",
  danger: "bg-danger-bg text-danger ring-danger/20",
  warning: "bg-warning-bg text-warning ring-warning/20",
  info: "bg-info-bg text-primary-dark ring-primary-dark/20",
};

export function Badge({
  tone = "neutral",
  dot = false,
  className,
  children,
}: {
  tone?: BadgeTone;
  /** Leading status dot (for states such as active / banned). */
  dot?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset",
        badgeTones[tone],
        className,
      )}
    >
      {dot && <span aria-hidden className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

export function Chip({
  selected,
  onClick,
  children,
  onRemove,
  removeLabel = "Remove",
}: {
  selected?: boolean;
  onClick?: () => void;
  children: ReactNode;
  onRemove?: () => void;
  removeLabel?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-8 items-center gap-1 rounded-full border text-[13px] font-medium transition-colors",
        selected
          ? "border-primary-dark/30 bg-info-bg text-primary-dark"
          : "border-line-strong bg-paper text-ink-2 hover:text-ink",
      )}
    >
      {onClick ? (
        <button
          type="button"
          aria-pressed={selected}
          onClick={onClick}
          className={cn(
            "inline-flex h-full items-center gap-1.5 rounded-full px-3",
            selected ? "hover:bg-primary/5" : "hover:bg-subtle",
          )}
        >
          {selected && <Check aria-hidden className="-ml-0.5 size-3.5" />}
          {children}
        </button>
      ) : (
        <span className="px-3">{children}</span>
      )}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className="-ml-1 mr-1 inline-flex size-6 items-center justify-center rounded-full hover:bg-hover"
        >
          ×
        </button>
      )}
    </span>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="px-6 py-16 text-center">
      <p className="text-base font-medium text-ink">{title}</p>
      {children && <p className="mt-1 text-sm text-ink-3">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cn("border-line", className)} />;
}
